import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { CreateNewChat, GetUserUUID, CharacterPayload } from '@components'
import { Utils as utils } from '@client/utility'

interface ChatRow {
  chat_uuid: string
  name: string
  char_uuid: string
  profile_image?: string | null
}

interface ChatMessageRow {
  char_message: string | null
}

export function useChatSidebar(chat_uuid: string) {
  const [recentChats, setRecentChats] = useState<
    (ChatRow & { lastMessage?: string })[]
  >([])
  const [loading, setLoading] = useState(true)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedChat, setSelectedChat] = useState<ChatRow | null>(null)
  const [collapsed, setCollapsed] = useState(false)
  const [charModalOpen, setCharModalOpen] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)

  const navigate = useNavigate()

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    chat: ChatRow
  ) => {
    setAnchorEl(event.currentTarget)
    setSelectedChat(chat)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedChat(null)
  }

  const handleDeleteChat = async () => {
    if (!selectedChat?.chat_uuid) return

    try {
      await utils.db.remove<ChatRow>({
        tables: 'chats',
        match: { chat_uuid: selectedChat.chat_uuid },
      })

      setRecentChats((prev) =>
        prev.filter((c) => c.chat_uuid !== selectedChat.chat_uuid)
      )
    } catch (err) {
      Sentry.captureException(err)
    } finally {
      handleMenuClose()
    }
  }

  const handleNewChat = async (char_uuid?: string) => {
    const uuid = char_uuid ?? selectedChat?.char_uuid
    if (!uuid) return

    setModalLoading(true)

    try {
      const userUuid = await GetUserUUID()
      if (!userUuid) return

      const result = await CreateNewChat(uuid, userUuid)
      if (result.chat_uuid) navigate(`/chat/${result.chat_uuid}`)
    } catch (err) {
      Sentry.captureException(err)
    } finally {
      setModalLoading(false)
      setCharModalOpen(false)
      handleMenuClose()
    }
  }

  const handlePreviousChats = (char_uuid?: string) => {
    const uuid = char_uuid ?? selectedChat?.char_uuid
    if (!uuid) return
    navigate(`/archive?char_uuid=${uuid}`)
    setCharModalOpen(false)
  }

  useEffect(() => {
    const loadChats = async () => {
      setLoading(true)

      try {
        const result = await utils.db.select<ChatRow>({
          tables: 'chats',
          columns: 'chat_uuid, name, char_uuid, profile_image',
          match: { is_temporary: false },
          orderBy: { column: 'created_at', ascending: false },
          range: { from: 0, to: 29 },
        })

        const chats = result?.data ?? []

        const withMessages = await Promise.all(
          chats.map(async (chat) => {
            const msgResult = await utils.db.select<ChatMessageRow>({
              tables: 'chat_messages',
              columns: 'char_message',
              match: { chat_uuid: chat.chat_uuid },
              orderBy: { column: 'created_at', ascending: false },
              range: { from: 0, to: 0 },
              nocache: true,
            })

            const msgs = msgResult?.data ?? []

            return {
              ...chat,
              lastMessage: msgs.length
                ? msgs[0].char_message ?? ''
                : '',
            }
          })
        )

        setRecentChats(withMessages)
      } catch (err) {
        Sentry.captureException(err)
      } finally {
        setLoading(false)
      }
    }

    loadChats()
  }, [chat_uuid])

  return {
    recentChats,
    loading,
    anchorEl,
    selectedChat,
    collapsed,
    charModalOpen,
    modalLoading,
    setCollapsed,
    setCharModalOpen,
    handleMenuOpen,
    handleMenuClose,
    handleDeleteChat,
    handleNewChat,
    handlePreviousChats,
  }
}